import { isLikelyNativeAppShell } from '@/lib/native-shell-detect';
import {
  checkCapacitorRuntimePermissions,
  requestCapacitorPermissionStep,
} from '@/lib/native-permissions-capacitor';
import {
  checkRuntimePermissions,
  isRuntimePermissionsPluginAvailable,
  openNativeAppSettings,
  requestRuntimeCamera,
  requestRuntimeLocation,
  requestRuntimeMicrophone,
  requestRuntimeNotifications,
  requestRuntimePhotos,
  type RuntimePermissionCheck,
  type RuntimePermissionState,
} from '@/lib/runtime-permissions-plugin';
import {
  applyCapacitorStatusBarTheme,
  scheduleCapacitorStatusBarSync,
  themeFromDocumentClassList,
} from '@/lib/native-status-bar-colors';

const PERMS_DONE_KEY = 'ppc_native_runtime_perms_v3';
const PERMS_GATE_EVENT = 'ppc:runtime-perms-gate';

export type NativePermissionStep = 'location' | 'camera' | 'photos' | 'microphone' | 'notifications';

export type NativePermissionStepResult = {
  step: NativePermissionStep;
  state: RuntimePermissionState | 'unsupported';
};

const STEP_CHECK_KEY: Record<NativePermissionStep, keyof RuntimePermissionCheck> = {
  location: 'location',
  camera: 'camera',
  photos: 'photos',
  microphone: 'microphone',
  notifications: 'notifications',
};

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function wasNativePermissionsFlowCompleted(): boolean {
  return storageGet(PERMS_DONE_KEY) === '1';
}

export function shouldShowNativePermissionsOnboarding(): boolean {
  return isLikelyNativeAppShell() && !wasNativePermissionsFlowCompleted();
}

function markNativePermissionsCompleted(): void {
  storageSet(PERMS_DONE_KEY, '1');
  notifyRuntimePermissionsGate();
}

export function notifyRuntimePermissionsGate(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PERMS_GATE_EVENT));
}

export function waitForRuntimePermissionsGate(timeoutMs = 10 * 60_000): Promise<void> {
  if (typeof window === 'undefined' || wasNativePermissionsFlowCompleted()) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener(PERMS_GATE_EVENT, onGate);
      window.clearTimeout(timer);
      resolve();
    };
    const onGate = () => done();
    const timer = window.setTimeout(done, timeoutMs);
    window.addEventListener(PERMS_GATE_EVENT, onGate);
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Истина с устройства (Android plugin или Capacitor check), не только ответ request(). */
export async function isNativePermissionGranted(step: NativePermissionStep): Promise<boolean> {
  const check = (await checkRuntimePermissions()) ?? (await checkCapacitorRuntimePermissions());
  if (!check) return false;
  return check[STEP_CHECK_KEY[step]] === 'granted';
}

/** Do not coerce `prompt` to `denied` — that opens Settings before any iOS system dialog. */
export function resolveNativePermissionState(
  raw: RuntimePermissionState,
  grantedByCheck: boolean,
): RuntimePermissionState {
  if (grantedByCheck || raw === 'granted') return 'granted';
  return raw;
}

async function normalizeStepState(
  step: NativePermissionStep,
  raw: RuntimePermissionState,
): Promise<RuntimePermissionState | 'unsupported'> {
  await delay(400);
  const grantedByCheck = await isNativePermissionGranted(step);
  return resolveNativePermissionState(raw, grantedByCheck);
}

async function requestViaAndroidPlugin(step: NativePermissionStep): Promise<RuntimePermissionState> {
  switch (step) {
    case 'location':
      return requestRuntimeLocation();
    case 'camera':
      return requestRuntimeCamera();
    case 'photos':
      return requestRuntimePhotos();
    case 'microphone':
      return requestRuntimeMicrophone();
    case 'notifications':
      return requestRuntimeNotifications();
    default:
      return 'prompt';
  }
}

export type WebMediaKind = 'audio' | 'video';

function webMediaConstraints(kind: WebMediaKind): MediaStreamConstraints {
  if (kind === 'audio') {
    return { audio: true };
  }
  return {
    video: { facingMode: 'user', width: 480, height: 480 },
    audio: true,
  };
}

async function getUserMediaWithRetry(
  constraints: MediaStreamConstraints,
  attempts = 3,
): Promise<MediaStream> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      if (attempt < attempts - 1) {
        await delay(250 * (attempt + 1));
      }
    }
  }
  throw lastError;
}

/** Один вызов getUserMedia после нативных разрешений — для записи голоса / видеокружка. */
export async function acquireWebMediaStream(kind: WebMediaKind): Promise<MediaStream | null> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return null;
  }

  if (isLikelyNativeAppShell()) {
    if (kind === 'audio') {
      if (!(await isNativePermissionGranted('microphone'))) {
        const { state } = await requestNativePermissionStep('microphone');
        if (state !== 'granted' && !(await isNativePermissionGranted('microphone'))) {
          return null;
        }
      }
    } else {
      if (!(await isNativePermissionGranted('camera'))) {
        const cam = await requestNativePermissionStep('camera');
        if (cam.state !== 'granted' && !(await isNativePermissionGranted('camera'))) {
          return null;
        }
      }
      if (!(await isNativePermissionGranted('microphone'))) {
        const mic = await requestNativePermissionStep('microphone');
        if (mic.state !== 'granted' && !(await isNativePermissionGranted('microphone'))) {
          return null;
        }
      }
    }
    await delay(150);
  }

  try {
    return await getUserMediaWithRetry(webMediaConstraints(kind));
  } catch {
    return null;
  }
}

export async function requestNativePermissionStep(
  step: NativePermissionStep,
): Promise<NativePermissionStepResult> {
  if (!isLikelyNativeAppShell()) {
    return { step, state: 'unsupported' };
  }

  const raw = isRuntimePermissionsPluginAvailable()
    ? await requestViaAndroidPlugin(step)
    : await requestCapacitorPermissionStep(STEP_CHECK_KEY[step]);

  const state = await normalizeStepState(step, raw);
  return { step, state };
}

export async function ensureMicrophoneAccess(): Promise<boolean> {
  const stream = await acquireWebMediaStream('audio');
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    return true;
  }
  return false;
}

export async function ensureCameraAndMicrophoneAccess(): Promise<boolean> {
  const stream = await acquireWebMediaStream('video');
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    return true;
  }
  return false;
}

export function finishNativePermissionWizard(): void {
  markNativePermissionsCompleted();
}

export async function configureNativeStatusBar(): Promise<void> {
  if (!isLikelyNativeAppShell()) return;
  try {
    if (typeof document === 'undefined') return;
    await applyCapacitorStatusBarTheme(themeFromDocumentClassList(document.documentElement.classList));
  } catch {
    /* ignore */
  }
}

export { scheduleCapacitorStatusBarSync, openNativeAppSettings };

export async function requestMicrophonePermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  const { state } = await requestNativePermissionStep('microphone');
  if (state === 'unsupported') return 'unsupported';
  if (state === 'granted') return 'granted';
  return 'denied';
}
